import { useEffect, useRef, type RefObject } from 'react';
import {
  STATE_PARAMS,
  createParticles,
  drawFrame,
  lerpParams,
  simulateVoice,
  type FieldParams,
  type Particle,
} from '../utils/particleField';
import { createLife, nightness, pokeLife, stepLife } from '../utils/annaLife';
import { speechVoiceLevel } from '../utils/voiceSignal';
import { hexToRgb } from '../utils/color';
import type { Aliveness, AvatarState, AvatarThread } from '../types/anna';

export interface AvatarRenderOptions {
  state: AvatarState;
  accent: string;
  threads: AvatarThread[];
  reduced: boolean;
  pulseKey: number;
  aliveness: Aliveness;
}

const clamp = (v: number, min: number, max: number) => Math.max(min, Math.min(max, v));
const ALIVENESS_AMOUNT: Record<Aliveness, number> = { calm: 0.45, lively: 1 };

export function useAvatarRenderer(canvasRef: RefObject<HTMLCanvasElement>, options: AvatarRenderOptions) {
  const optionsRef = useRef(options);
  optionsRef.current = options;
  const clockRef = useRef(0);
  const waveStartRef = useRef<number | null>(null);

  useEffect(() => {
    if (options.pulseKey > 0) waveStartRef.current = clockRef.current;
  }, [options.pulseKey]);

  useEffect(() => {
    if (options.state === 'meldt') waveStartRef.current = clockRef.current;
  }, [options.state]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    let raf = 0;
    let last = performance.now();
    let visible = true;
    let cssW = 1;
    let cssH = 1;
    let particles: Particle[] = [];
    let particleBase = 0;
    const current: FieldParams = { ...STATE_PARAMS[optionsRef.current.state] };
    let angle = 0;
    let breathPhase = 0;
    let voice = 0;
    const pointer = { x: 0, y: 0 };
    const parallax = { x: 0, y: 0 };
    const gazeDir = { x: 0, y: 0 };
    let proximity = 0;
    let pointerMovedAt = -Infinity;
    const life = createLife(optionsRef.current.state);
    let night = nightness();
    let nightCheckedAt = 0;

    const resize = () => {
      const rect = canvas.getBoundingClientRect();
      cssW = Math.max(1, rect.width);
      cssH = Math.max(1, rect.height);
      const side = Math.min(cssW, cssH);
      // Large wall-sized canvases cap the pixel ratio to keep the frame budget.
      const dpr = Math.min(side > 640 ? 1.5 : 2, window.devicePixelRatio || 1);
      canvas.width = Math.round(cssW * dpr);
      canvas.height = Math.round(cssH * dpr);
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      if (particles.length === 0 || Math.abs(side - particleBase) > 40) {
        particles = createParticles(Math.round(clamp(side * 1.5, 160, 1100)));
        particleBase = side;
      }
    };

    const ro = new ResizeObserver(resize);
    ro.observe(canvas);
    resize();

    const io = new IntersectionObserver(
      ([entry]) => {
        visible = entry.isIntersecting;
      },
      { rootMargin: '120px' },
    );
    io.observe(canvas);

    /** Distance from Anna's centre in units of her radius, plus direction. */
    const locate = (e: PointerEvent) => {
      const r = canvas.getBoundingClientRect();
      const cx = r.left + r.width / 2;
      const cy = r.top + r.height / 2;
      const half = Math.max(1, Math.min(r.width, r.height) / 2);
      const dx = e.clientX - cx;
      const dy = e.clientY - cy;
      return { cx, cy, dx, dy, dist: Math.hypot(dx, dy) / half };
    };

    const onPointer = (e: PointerEvent) => {
      const { cx, cy, dx, dy, dist } = locate(e);
      pointer.x = clamp((e.clientX - cx) / (window.innerWidth / 2), -1, 1);
      pointer.y = clamp((e.clientY - cy) / (window.innerHeight / 2), -1, 1);
      // Gaze: direction toward the pointer, strength eases in with distance
      const len = Math.hypot(dx, dy) || 1;
      const strength = clamp(dist / 1.1, 0, 1);
      gazeDir.x = (dx / len) * strength;
      gazeDir.y = (dy / len) * strength;
      proximity = clamp(1 - (dist - 0.5) / 1.3, 0, 1);
      pointerMovedAt = performance.now();
    };

    const onPointerDown = (e: PointerEvent) => {
      onPointer(e);
      const { dist } = locate(e);
      const radius = STATE_PARAMS[optionsRef.current.state].radius;
      if (dist <= Math.max(0.3, radius * 0.9) && !optionsRef.current.reduced) {
        pokeLife(life, ALIVENESS_AMOUNT[optionsRef.current.aliveness]);
        waveStartRef.current = clockRef.current;
      }
    };

    const onLeave = () => {
      proximity = 0;
    };

    window.addEventListener('pointermove', onPointer, { passive: true });
    window.addEventListener('pointerdown', onPointerDown, { passive: true });
    document.addEventListener('pointerleave', onLeave);

    const loop = (now: number) => {
      raf = requestAnimationFrame(loop);
      const dt = Math.min(0.05, (now - last) / 1000);
      last = now;
      if (!visible) return;

      const o = optionsRef.current;
      clockRef.current += dt;
      const t = clockRef.current;

      if (now - nightCheckedAt > 30000) {
        night = nightness();
        nightCheckedAt = now;
      }

      lerpParams(current, STATE_PARAMS[o.state], 1 - Math.exp(-dt * (o.reduced ? 7 : 3.2)));
      if (!o.reduced) angle += current.spin * dt;

      // Breathing is never metronomic: a slow, irregular wander in pace — slower at night.
      const irregular = o.reduced ? 1 : 1 + 0.08 * Math.sin(t * 0.13) + 0.05 * Math.sin(t * 0.31 + 2);
      const period = Math.max(0.5, current.breathPeriod) * irregular * (1 + night * 0.25);
      breathPhase += (dt / period) * Math.PI * 2;

      const live = o.state === 'spreekt' ? speechVoiceLevel() : null;
      const voiceTarget = live ?? simulateVoice(t, o.state);
      voice += (voiceTarget - voice) * Math.min(1, dt * (live !== null ? 18 : 14));

      const pk = o.reduced ? 0 : Math.min(1, dt * 3);
      parallax.x += (pointer.x - parallax.x) * pk;
      parallax.y += (pointer.y - parallax.y) * pk;

      const lifeFrame = stepLife(life, {
        t,
        dt,
        state: o.state,
        reduced: o.reduced,
        amount: ALIVENESS_AMOUNT[o.aliveness],
        pointer: gazeDir,
        pointerAge: (now - pointerMovedAt) / 1000,
        proximity: (now - pointerMovedAt) / 1000 < 6 ? proximity : 0,
      });

      let wave: number | null = null;
      if (waveStartRef.current !== null) {
        const w = (t - waveStartRef.current) / 1.8;
        if (w >= 1) waveStartRef.current = null;
        else wave = Math.max(0, w);
      }

      drawFrame({
        ctx,
        width: cssW,
        height: cssH,
        t,
        angle,
        breathPhase,
        p: current,
        particles,
        accent: hexToRgb(o.accent),
        voice,
        wave,
        parallax,
        reduced: o.reduced,
        threads: o.threads.map((th) => ({ color: hexToRgb(th.color), speed: th.speed })),
        life: lifeFrame,
        night,
      });
    };
    raf = requestAnimationFrame(loop);

    return () => {
      cancelAnimationFrame(raf);
      ro.disconnect();
      io.disconnect();
      window.removeEventListener('pointermove', onPointer);
      window.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('pointerleave', onLeave);
    };
  }, [canvasRef]);
}
